// The original app used red for branding and a separate blue Fluent ramp.
// Keep those roles separate: red -> purple; blue -> teal.
// Single source for runtime web brand colours. CSS receives the same values
// through applyBrandCssVariables before React renders; components import tokens.
export const BRAND_PRIMARY = "#3a0067";
export const BRAND_PRIMARY_HOVER = "#2e0050";
export const BRAND_SECONDARY = "#006b70";
export const BRAND_SECONDARY_HOVER = "#006065";
export const BRAND_SECONDARY_DARK = "#004c50";
export const BRAND_SECONDARY_LIGHT = "#e6f3f2";

// The original decorative artwork's base is RGB 214,40,40. Map that to
// primary while retaining white and alpha; navbar/cards keep their 7% lift.
export const getBrandWaveMatrix = (primary = BRAND_PRIMARY, whiteLift = 0) => {
  if (!/^#[0-9a-f]{6}$/i.test(primary)) throw new Error("Brand primary must be a six-digit hex colour");
  const channels = [1, 3, 5].map(start => parseInt(primary.slice(start, start + 2), 16));
  return channels.flatMap(channel => {
    const redWeight = (channel - 40) / (214 - 40);
    return [redWeight * (1 - whiteLift), (1 - redWeight) * (1 - whiteLift), 0, 0, whiteLift];
  }).concat([0, 0, 0, 1, 0]).map(value => Number(value.toFixed(9))).join(" ");
};

export const BRAND_CSS_VARIABLES = {
  "--brand-primary": BRAND_PRIMARY,
  "--brand-primary-hover": BRAND_PRIMARY_HOVER,
  "--brand-secondary": BRAND_SECONDARY,
  "--brand-secondary-hover": BRAND_SECONDARY_HOVER,
  "--brand-secondary-dark": BRAND_SECONDARY_DARK,
  "--brand-secondary-light": BRAND_SECONDARY_LIGHT,
};

export const applyBrandCssVariables = (root: HTMLElement) => {
  Object.entries(BRAND_CSS_VARIABLES).forEach(([property, color]) => {
    root.style.setProperty(property, color);
  });
};

export const THEME = {
  palette: {
    themePrimary: BRAND_SECONDARY,
    themeLighterAlt: "#f3fafa",
    themeLighter: "#cce5e6",
    themeLight: "#99cbcd",
    themeTertiary: "#4d9da1",
    themeSecondary: "#197b80",
    themeDarkAlt: BRAND_SECONDARY_HOVER,
    themeDark: "#005155",
    themeDarker: "#003c3f",
    neutralLighterAlt: "#f8f8f8",
    neutralLighter: "#f4f4f4",
    neutralLight: "#eaeaea",
    neutralQuaternaryAlt: "#dadada",
    neutralQuaternary: "#d0d0d0",
    neutralTertiaryAlt: "#c8c8c8",
    neutralTertiary: "#595959",
    neutralSecondary: "#373737",
    neutralPrimaryAlt: "#2f2f2f",
    neutralPrimary: "#000",
    neutralDark: "#151515",
    black: "#0b0b0b",
    white: "#fff",
  },
};

// Underlined authentication fields must not fall back to Fluent's red
// validation border. Error messages and aria-invalid still identify errors.
// Both Fluent versions used by the app accept this shared style function.
export const authTextFieldStyles = ({ disabled }: { disabled?: boolean }) => {
  const borderColor = disabled
    ? THEME.palette.neutralTertiaryAlt
    : BRAND_PRIMARY;
  return {
    wrapper: {
      borderBottom: `1px solid ${borderColor}`,
      borderBottomColor: borderColor,
      selectors: {
        ":hover": { borderBottomColor: borderColor },
        ":after": { borderBottomColor: borderColor },
      },
    },
    fieldGroup: {
      border: "none",
      selectors: { ":hover": { border: "none" } },
    },
  };
};
export const GS = {
  p8: {
    padding: 8,
  },
  bgWhite: {
    background: "#fff",
  },
  icon: { fontSize: 50, height: 50, width: 50, margin: "0 25px" },
  header: {
    fontSize: "28px",
    fontWeight: "400",
  },
};
